/**
 * ONE BORROWING REQUEST: where it is, what it is secured on, and who has offered (FR-U-CRD-003).
 *
 * ## THE OFFER COMPARISON IS THE POINT OF THIS SCREEN
 *
 * Multiple financiers bid on the same request, so the borrower's job here is to COMPARE and then
 * choose. FR-U-CRD-014 requires the total cost of credit in a key facts format before acceptance, so
 * each offer shows its principal, its interest, its fees itemised and the total repayable, and those
 * figures come from the offer itself rather than being recomputed here: they were frozen when the
 * financier made the offer, and a number this screen derived could drift from the one the borrower
 * is actually agreeing to.
 *
 * ## ACCEPTING ONE DECLINES THE REST, AND THE SCREEN SAYS SO BEFORE THE CLICK
 *
 * That happens server-side in a single transaction. Saying it here is not the control; it is so the
 * borrower is not surprised by it.
 *
 * ## ACCEPTANCE IS STILL NOT A LOAN
 *
 * After the borrower accepts, the request sits with the financier for final approval. The timeline
 * shows that stage explicitly rather than jumping to "approved", because a borrower who believes
 * they have a loan and does not is the worst outcome this flow can produce.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, LipaApiError, type CreditRequestDetail, type FinancingOffer } from '@/lib/api';
import { PageHeader, SectionTitle } from '@/components/dash';
import { StatusPill } from '@/components/status';
import { ErrorState, LoadingCard } from '@/components/states';
import { Reveal } from '@/components/motion';
import { fmtDateTime, fmtDateShort } from '@/lib/format';
import { formatWireAmount } from '@/lib/money';

const STATE_LABEL: Record<string, string> = {
  draft: 'Draft',
  submitted: 'Submitted',
  under_review: 'Under review',
  collateral_verification: 'Collateral verification',
  trust_review: 'Trust review',
  open_for_financing: 'Open for financing',
  financing_interest_received: 'Offers received',
  financing_selected: 'Offer selected',
  borrower_acceptance_pending: 'Awaiting your acceptance',
  dcp_approval_pending: 'With the financier for approval',
  approved: 'Approved',
  cancelled: 'Cancelled',
  rejected: 'Declined',
  expired: 'Expired',
};

/** The stages a borrower is shown, in order. Derived from the request's own transition log. */
const JOURNEY = [
  { key: 'submitted', label: 'Submitted' },
  { key: 'collateral_verification', label: 'Collateral verified' },
  { key: 'trust_review', label: 'Trust hold placed' },
  { key: 'open_for_financing', label: 'Open for financing' },
  { key: 'financing_selected', label: 'Offer accepted' },
  { key: 'approved', label: 'Approved' },
];

export default function CreditRequestsId() {
  const { id = '' } = useParams();
  const [request, setRequest] = useState<CreditRequestDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setRequest(await api.creditMarket.request(id));
    } catch (e) {
      setError(e instanceof LipaApiError ? e.detail.message : 'We could not load this request.');
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) return <ErrorState body={error} onRetry={() => void load()} />;
  if (!request) return <LoadingCard lines={5} />;

  const reached = new Set(request.timeline.map((t) => t.to_state));
  const liveOffers = request.offers.filter((o) => ['submitted', 'under_review'].includes(o.state));
  const accepted = request.offers.find((o) => o.state === 'accepted');
  const canAccept = ['financing_interest_received', 'financing_selected'].includes(request.state);
  const canCancel = !['approved', 'cancelled', 'rejected', 'expired'].includes(request.state);

  async function accept(offerId: string) {
    setBusy(offerId);
    setError(null);
    try {
      setRequest(await api.creditMarket.acceptOffer(id, offerId));
      setConfirming(null);
    } catch (e) {
      setError(e instanceof LipaApiError ? e.detail.message : 'We could not accept that offer.');
    } finally {
      setBusy(null);
    }
  }

  async function cancel() {
    setBusy('cancel');
    try {
      await api.creditMarket.cancelRequest(id);
      await load();
    } catch (e) {
      setError(e instanceof LipaApiError ? e.detail.message : 'We could not cancel this request.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="mx-auto max-w-2xl space-y-5">
      <PageHeader
        title={request.product_name ?? 'Borrowing request'}
        subtitle={`${request.reference} · ${request.tenor_months} months`}
        actions={<StatusPill status={request.state} label={STATE_LABEL[request.state]} />}
      />

      {error && (
        <div
          role="alert"
          className="rounded-card border border-danger/40 bg-danger/5 p-3 text-sm text-ink"
        >
          {error}
        </div>
      )}

      <section className="rounded-card border border-border bg-surface p-4">
        <dl className="space-y-2">
          <Row label="Amount requested" value={`KES ${formatWireAmount(request.amount_minor)}`} />
          <Row
            label="Eligible collateral"
            value={`KES ${formatWireAmount(request.eligible_collateral_minor ?? '0')}`}
          />
          <Row
            label="Maximum financing"
            value={`KES ${formatWireAmount(request.max_financing_minor ?? '0')}`}
            accent
          />
          <Row label="Purpose" value={request.purpose} />
          {request.financing_deadline && (
            <Row label="Open until" value={fmtDateShort(request.financing_deadline)} />
          )}
          {request.rejected_reason && <Row label="Reason" value={request.rejected_reason} danger />}
        </dl>
      </section>

      {/* WHERE IT IS. */}
      <section className="rounded-card border border-border bg-surface p-4">
        <p className="text-sm font-medium text-ink">Progress</p>
        <ol className="mt-3 space-y-2.5">
          {JOURNEY.map((stage) => {
            const done = reached.has(stage.key);
            return (
              <li key={stage.key} className="flex items-center gap-2.5">
                <span
                  aria-hidden
                  className={`h-2 w-2 shrink-0 rounded-full ${done ? 'bg-brand' : 'bg-border'}`}
                />
                <span className={`text-micro ${done ? 'text-ink' : 'text-ink-subtle'}`}>
                  {stage.label}
                </span>
              </li>
            );
          })}
        </ol>
      </section>

      {/* THE COLLATERAL, AND ITS LOCK STATUS (FR-U-CRD-006). */}
      <section>
        <SectionTitle>Collateral</SectionTitle>
        <ul className="space-y-2">
          {request.collateral.map((c) => (
            <li
              key={c.id}
              className="rounded-card border border-border bg-surface p-3.5 shadow-card"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-ink">{c.asset_name}</p>
                  <p className="text-nano text-ink-subtle">
                    {(c.pledged_bps / 100).toFixed(2)}% of the asset pledged · lien{' '}
                    {c.lien_priority}
                  </p>
                </div>
                <StatusPill
                  status={c.state}
                  label={c.state === 'encumbered' ? 'Held by the Trust' : undefined}
                />
              </div>
              <div className="mt-2.5 flex items-center justify-between border-t border-border pt-2.5 text-micro">
                <span className="text-ink-subtle">
                  Valued at KES {formatWireAmount(c.valuation_minor)}
                </span>
                <span className="tabular text-ink">
                  KES {formatWireAmount(c.eligible_minor)} eligible
                </span>
              </div>
            </li>
          ))}
        </ul>
      </section>

      {/* THE OFFERS. */}
      {(liveOffers.length > 0 || accepted) && (
        <section>
          <SectionTitle>Financing offers</SectionTitle>

          {canAccept && liveOffers.length > 1 && (
            <p className="mb-2.5 rounded-card bg-surface-sunken p-2.5 text-micro text-ink-muted">
              Accepting one offer declines the others. Compare the total repayable, not just the
              rate: fees differ between financiers.
            </p>
          )}

          <ul className="space-y-3">
            {(accepted ? [accepted, ...liveOffers] : liveOffers).map((offer) => (
              <li key={offer.id}>
                <Reveal>
                  <OfferCard
                    offer={offer}
                    canAccept={canAccept && offer.state !== 'accepted'}
                    busy={busy === offer.id}
                    confirming={confirming === offer.id}
                    onAsk={() => setConfirming(offer.id)}
                    onCancelAsk={() => setConfirming(null)}
                    onAccept={() => void accept(offer.id)}
                    rivals={liveOffers.length - 1}
                  />
                </Reveal>
              </li>
            ))}
          </ul>
        </section>
      )}

      {request.state === 'dcp_approval_pending' && (
        <div className="rounded-card border border-info/30 bg-info/5 p-3 text-sm text-ink-muted">
          <span className="font-medium text-info">With the financier.</span> You have accepted the
          offer. The financier is now completing its own final checks. Nothing is disbursed and no
          loan exists until it approves.
        </div>
      )}

      {/* THE TRAIL. Every step, who took it, and when. */}
      <section>
        <SectionTitle>History</SectionTitle>
        <ol className="space-y-2">
          {[...request.timeline].reverse().map((t, i) => (
            <li key={`${t.to_state}-${i}`} className="flex items-baseline justify-between gap-3">
              <span className="min-w-0 text-micro text-ink">
                {STATE_LABEL[t.to_state] ?? t.to_state}
                {t.reason && <span className="text-ink-subtle"> &mdash; {t.reason}</span>}
              </span>
              <span className="shrink-0 text-nano text-ink-subtle">
                {fmtDateTime(t.occurred_at)}
              </span>
            </li>
          ))}
        </ol>
      </section>

      <div className="flex gap-2">
        <Link
          to="/credit"
          className="rounded-field border border-border px-3.5 py-2 text-sm font-medium text-ink"
        >
          Back to Credit
        </Link>
        {canCancel && (
          <button
            type="button"
            disabled={busy === 'cancel'}
            onClick={() => void cancel()}
            className="rounded-field border border-danger/40 px-3.5 py-2 text-sm font-medium text-danger disabled:opacity-40"
          >
            {busy === 'cancel' ? 'Cancelling…' : 'Cancel request'}
          </button>
        )}
      </div>
    </div>
  );
}

function OfferCard({
  offer,
  canAccept,
  busy,
  confirming,
  onAsk,
  onCancelAsk,
  onAccept,
  rivals,
}: {
  offer: FinancingOffer;
  canAccept: boolean;
  busy: boolean;
  confirming: boolean;
  onAsk: () => void;
  onCancelAsk: () => void;
  onAccept: () => void;
  rivals: number;
}) {
  const facts = offer.key_facts;
  return (
    <article
      className={`rounded-card border p-4 shadow-card ${
        offer.state === 'accepted' ? 'border-brand bg-brand/5' : 'border-border bg-surface'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-ink">{offer.financer_name}</p>
          <p className="text-nano text-ink-subtle">{offer.product_name}</p>
        </div>
        {offer.state === 'accepted' ? (
          <StatusPill status="accepted" label="Accepted" />
        ) : (
          <span className="tabular shrink-0 rounded-field bg-brand/10 px-2 py-1 text-micro font-medium text-brand">
            {(offer.rate_bps / 100).toFixed(2)}% p.a.
          </span>
        )}
      </div>

      {/* KEY FACTS (FR-U-CRD-014). Itemised, and taken from the offer rather than recomputed. */}
      <dl className="mt-3 space-y-1.5 border-t border-border pt-3">
        <Row label="Amount" value={`KES ${formatWireAmount(offer.amount_minor)}`} />
        <Row label="Tenor" value={`${offer.tenor_months} months`} />
        <Row label="Interest" value={`KES ${formatWireAmount(facts.interest_minor)}`} />
        {facts.fee_items.map((fee) => (
          <Row
            key={fee.code}
            label={fee.label}
            value={`KES ${formatWireAmount(fee.amount_minor)}`}
          />
        ))}
        <Row
          label="Total cost of credit"
          value={`KES ${formatWireAmount(facts.cost_of_credit_minor)}`}
        />
        <Row
          label="Total repayable"
          value={`KES ${formatWireAmount(offer.total_repayable_minor)}`}
          accent
        />
      </dl>

      {offer.conditions.length > 0 && (
        <ul className="mt-2.5 space-y-1 border-t border-border pt-2.5">
          {offer.conditions.map((c) => (
            <li key={c} className="text-nano leading-relaxed text-ink-muted">
              &middot; {c}
            </li>
          ))}
        </ul>
      )}

      <p className="mt-2.5 text-nano text-ink-subtle">
        Offer expires {fmtDateShort(offer.expires_at)}
      </p>

      {canAccept &&
        (confirming ? (
          <div className="mt-3 rounded-field bg-surface-sunken p-3">
            <p className="text-micro leading-relaxed text-ink">
              Accept {offer.financer_name}&rsquo;s offer at{' '}
              {(offer.rate_bps / 100).toFixed(2)}% over {offer.tenor_months} months, repaying KES{' '}
              {formatWireAmount(offer.total_repayable_minor)} in total?
              {rivals > 0 && (
                <>
                  {' '}
                  This declines {rivals} other offer{rivals === 1 ? '' : 's'}.
                </>
              )}
            </p>
            <div className="mt-2.5 flex gap-2">
              <button
                type="button"
                onClick={onCancelAsk}
                className="rounded-field border border-border px-3 py-1.5 text-micro font-medium text-ink"
              >
                Not yet
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={onAccept}
                className="flex-1 rounded-field bg-primary px-3 py-1.5 text-micro font-medium text-white disabled:opacity-40"
              >
                {busy ? 'Accepting…' : 'Accept this offer'}
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={onAsk}
            className="mt-3 w-full rounded-field bg-primary px-3 py-2 text-micro font-medium text-white transition hover:bg-primary-light"
          >
            Accept this offer
          </button>
        ))}
    </article>
  );
}

function Row({
  label,
  value,
  accent,
  danger,
}: {
  label: string;
  value: string;
  accent?: boolean;
  danger?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="shrink-0 text-micro text-ink-subtle">{label}</dt>
      <dd
        className={`tabular min-w-0 truncate text-right text-micro font-medium ${
          danger ? 'text-danger' : accent ? 'text-brand' : 'text-ink'
        }`}
      >
        {value}
      </dd>
    </div>
  );
}
